import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Satellite, 
  Map, 
  Users, 
  ChevronRight, 
  FileText, 
  Globe, 
  Award,
  Send,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#expertise", label: "Expertise" },
    { href: "#clients", label: "Clients" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? "nav-blur bg-background/80 border-b border-border" : ""
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
              <Globe className="w-6 h-6 text-primary" />
            </div>
            <span className="text-xl font-bold tracking-tight">FB Geomatics</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <Button 
              className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 rounded-none"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-cta-button"
            >
              Start a Project
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 pb-4"
              data-testid="mobile-menu"
            >
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block py-3 text-muted-foreground hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <Button 
                className="w-full mt-4 btn-glow bg-primary text-primary-foreground hover:bg-primary/90 rounded-none"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Start a Project
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center hero-gradient topo-grid overflow-hidden" data-testid="hero-section">
      {/* Background topographic decoration */}
      <div className="topo-lines" />
      
      {/* Radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.08)_0%,transparent_70%)]" />
      
      <div className="max-w-7xl mx-auto px-6 py-32 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center"
        >
          <span className="mono-label mb-6 inline-block" data-testid="hero-label">
            Geodesy & GNSS Specialists
          </span>
          
          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none mb-8 text-glow" data-testid="hero-title">
            Precision Geodesy
            <br />
            <span className="text-primary">&amp; GNSS Consulting</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed" data-testid="hero-subtitle">
            Scientific expertise for infrastructure and engineering. 
            Partnering with industry leaders like Altametris, SNCF &amp; NGE 
            for over 15 years.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 py-6 text-lg font-semibold"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-cta-button"
            >
              Start a Project
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-none px-8 py-6 text-lg border-border hover:bg-muted hover:border-primary/50"
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-secondary-button"
            >
              Explore Services
            </Button>
          </div>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-muted rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-primary rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Satellite,
      title: "GNSS Consulting",
      description: "High-precision positioning strategies and implementation. RTK, PPP, and network solutions for demanding applications.",
      features: ["Real-time positioning", "Network design", "Quality assurance"]
    },
    {
      icon: Map,
      title: "Geodetic Surveys",
      description: "Complex terrain mapping and geodetic analysis. From control networks to deformation monitoring.",
      features: ["Control networks", "Deformation studies", "Datum transformation"]
    },
    {
      icon: Users,
      title: "Specialized Training",
      description: "Upskilling teams in modern geomatics. Customized programs from fundamentals to advanced techniques.",
      features: ["GNSS fundamentals", "Advanced processing", "Field techniques"]
    }
  ];

  return (
    <section id="services" className="py-32 relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="mono-label mb-4 inline-block">What We Offer</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="services-title">
            Expert Services
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="service-card h-full bg-card border-border hover:border-primary/30 rounded-none" data-testid={`service-card-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded bg-primary/10 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{service.title}</h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trust/Expertise Section
const ExpertiseSection = () => {
  const stats = [
    { value: "15+", label: "Years Experience", icon: Award },
    { value: "50+", label: "Scientific Papers", icon: FileText },
    { value: "100%", label: "Client Satisfaction", icon: Users }
  ];

  return (
    <section id="expertise" className="py-32 relative overflow-hidden" data-testid="expertise-section">
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ 
          backgroundImage: "url('https://images.pexels.com/photos/9751100/pexels-photo-9751100.jpeg?auto=compress&cs=tinysrgb&h=650&w=940')" 
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="mono-label mb-4 inline-block">Why Choose Us</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-testid="expertise-title">
            Proven Expertise
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            With over 15 years of experience and numerous scientific publications, 
            we bring academic rigor and practical expertise to every project.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="text-center"
              data-testid={`stat-${index}`}
            >
              <Card className="glass border-border/50 rounded-none p-8">
                <CardContent className="p-0">
                  <div className="w-16 h-16 rounded bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <stat.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="text-5xl md:text-6xl font-black text-primary mb-3 text-glow">
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground font-medium uppercase tracking-wider text-sm">
                    {stat.label}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Scientific credentials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="glass border border-border/50 rounded-none p-8 max-w-3xl mx-auto">
            <FileText className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-3">Scientific Publications</h3>
            <p className="text-muted-foreground leading-relaxed">
              Our expertise is backed by extensive research and scientific paper production 
              in geodesy, GNSS positioning, and geomatics. Contributing to the advancement 
              of the field while solving real-world challenges.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Clients Section
const ClientsSection = () => {
  const clients = [
    { name: "Altametris", description: "Railway surveying innovation" },
    { name: "SNCF", description: "French national railway" },
    { name: "NGE", description: "Infrastructure engineering" }
  ];

  return (
    <section id="clients" className="py-32 relative bg-card/50" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="mono-label mb-4 inline-block">Trusted Partners</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="clients-title">
            Industry Leaders Trust Us
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {clients.map((client, index) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
              data-testid={`client-${index}`}
            >
              <div className="client-logo glass border border-border/30 rounded-none p-8 hover:border-primary/30 transition-all duration-300">
                <div className="text-3xl font-black tracking-tight mb-3">
                  {client.name}
                </div>
                <p className="text-sm text-muted-foreground">
                  {client.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-muted-foreground mt-12 max-w-2xl mx-auto"
        >
          Delivering precision geodesy solutions for major infrastructure projects 
          across France and beyond.
        </motion.p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    project_details: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24-48 hours."
      });
      setFormData({ name: "", company: "", email: "", project_details: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="mono-label mb-4 inline-block">Get Started</span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="contact-title">
            Get in Touch
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Ready to discuss your project? Fill out the form and we'll respond within 24-48 hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="mono-label mb-2 block">Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-glow rounded-none bg-card border-border focus:border-primary"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="mono-label mb-2 block">Company</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="input-glow rounded-none bg-card border-border focus:border-primary"
                    placeholder="Your company"
                    data-testid="contact-company-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="mono-label mb-2 block">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-glow rounded-none bg-card border-border focus:border-primary"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <label className="mono-label mb-2 block">Project Details *</label>
                <Textarea
                  name="project_details"
                  value={formData.project_details}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="input-glow rounded-none bg-card border-border focus:border-primary resize-none"
                  placeholder="Tell us about your project requirements..."
                  data-testid="contact-details-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-glow bg-primary text-primary-foreground hover:bg-primary/90 rounded-none py-6 text-lg font-semibold"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <div className="glass border border-border/50 rounded-none p-8 space-y-8">
              <div>
                <h3 className="text-2xl font-semibold mb-4">Work With Us</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Whether you need GNSS consulting, geodetic surveys, or specialized training, 
                  our team brings 15+ years of industry experience to your project.
                </p>
              </div>
              
              <Separator className="bg-border" />
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Global Reach</h4>
                    <p className="text-sm text-muted-foreground">
                      Serving clients across France and internationally
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Proven Track Record</h4>
                    <p className="text-sm text-muted-foreground">
                      Trusted by Altametris, SNCF, and NGE
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Scientific Excellence</h4>
                    <p className="text-sm text-muted-foreground">
                      50+ published scientific papers in geodesy and GNSS
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-border" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center">
              <Globe className="w-4 h-4 text-primary" />
            </div>
            <span className="font-bold">FB Geomatics</span>
          </div>
          
          <p className="text-sm text-muted-foreground text-center">
            &copy; {new Date().getFullYear()} FB Geomatics. Precision geodesy and GNSS consulting.
          </p>
          
          <div className="flex gap-6">
            <a href="#services" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Services
            </a>
            <a href="#expertise" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Expertise
            </a>
            <a href="#contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const Home = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <ExpertiseSection />
      <ClientsSection />
      <ContactSection />
      <Footer />
      <Toaster richColors position="top-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
